package org.zq.astrock_ai.ui.research.gushi.components

import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.horizontalScroll
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.heightIn
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.Dp
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import androidx.compose.ui.window.Dialog
import androidx.compose.ui.window.DialogProperties
import org.zq.astrock_ai.ui.research.gushi.StockColors
import org.zq.astrock_ai.ui.research.gushi.adaptiveDialogWidth
import org.zq.astrock_ai.ui.research.gushi.data.RecommendHistorySnapshot
import org.zq.astrock_ai.ui.research.gushi.data.RecommendHistoryStore
import org.zq.astrock_ai.ui.research.gushi.data.formatHistoryPrice
import org.zq.astrock_ai.ui.research.gushi.data.formatPriceRange
import org.zq.astrock_ai.ui.research.gushi.data.formatRecommendHistoryTime
import org.zq.astrock_ai.util.formatDecimal

private data class RecommendHistoryRow(
    val key: String,
    val atMs: Long,
    val code: String,
    val name: String,
    val recommendPrice: Double,
    val buyLow: Double,
    val buyHigh: Double,
    val sellLow: Double,
    val sellHigh: Double,
    val emptyRun: Boolean = false,
)

@Composable
fun RecommendHistoryDialog(
    entries: List<RecommendHistorySnapshot>,
    onDismiss: () -> Unit,
    onSelectCode: (code: String, name: String) -> Unit = { _, _ -> },
    loadCurrentPrices: suspend (List<String>) -> Map<String, Double> = { emptyMap() },
) {
    val dates = remember(entries) { RecommendHistoryStore.uniqueDates(entries) }
    var selectedDay by remember(dates) { mutableStateOf(dates.firstOrNull()?.epochDay) }
    val dayEntries = remember(entries, selectedDay) {
        val day = selectedDay ?: return@remember emptyList()
        RecommendHistoryStore.forDate(entries, day)
    }
    val rows = remember(dayEntries) { flattenHistoryRows(dayEntries) }
    var currentPrices by remember { mutableStateOf<Map<String, Double>>(emptyMap()) }
    var priceLoading by remember { mutableStateOf(false) }

    LaunchedEffect(rows) {
        val codes = rows.map { it.code }.filter { it.isNotBlank() }.distinct()
        if (codes.isEmpty()) {
            currentPrices = emptyMap()
            return@LaunchedEffect
        }
        priceLoading = true
        currentPrices = runCatching { loadCurrentPrices(codes) }.getOrDefault(emptyMap())
        priceLoading = false
    }

    val shape = RoundedCornerShape(14.dp)
    Dialog(
        onDismissRequest = onDismiss,
        properties = DialogProperties(usePlatformDefaultWidth = false),
    ) {
        Column(
            modifier = Modifier
                .adaptiveDialogWidth(700.dp)
                .heightIn(max = 640.dp)
                .background(Color.White, shape)
                .border(BorderStroke(1.dp, StockColors.Border), shape)
                .padding(16.dp),
        ) {
            Row(verticalAlignment = Alignment.CenterVertically) {
                Text(
                    "推荐历史",
                    color = StockColors.TextPrimary,
                    fontWeight = FontWeight.Bold,
                    fontSize = 18.sp,
                    modifier = Modifier.weight(1f),
                )
                Text(
                    "关闭",
                    color = StockColors.TextSecondary,
                    fontSize = 13.sp,
                    modifier = Modifier
                        .clickable(onClick = onDismiss)
                        .padding(6.dp),
                )
            }
            Spacer(Modifier.height(4.dp))
            Text(
                "本机保存全部荐股记录 · 点日期查看当日 · 点股票进入研究详情 · 涨幅=现价相对推荐价",
                color = StockColors.TextSecondary,
                fontSize = 11.sp,
            )
            Spacer(Modifier.height(10.dp))
            if (dates.isEmpty()) {
                Text(
                    "暂无日期",
                    color = StockColors.TextSecondary,
                    fontSize = 12.sp,
                )
            } else {
                Row(
                    horizontalArrangement = Arrangement.spacedBy(8.dp),
                    modifier = Modifier
                        .fillMaxWidth()
                        .horizontalScroll(rememberScrollState()),
                ) {
                    dates.forEach { item ->
                        val selected = selectedDay == item.epochDay
                        Text(
                            "${item.label} · ${item.count}",
                            color = if (selected) Color.White else StockColors.TextSecondary,
                            fontSize = 12.sp,
                            fontWeight = if (selected) FontWeight.SemiBold else FontWeight.Normal,
                            modifier = Modifier
                                .background(
                                    if (selected) StockColors.PrimaryBlue else Color(0xFFF3F4F6),
                                    RoundedCornerShape(999.dp),
                                )
                                .clickable { selectedDay = item.epochDay }
                                .padding(horizontal = 12.dp, vertical = 7.dp),
                        )
                    }
                }
            }
            Spacer(Modifier.height(10.dp))

            Column(
                modifier = Modifier
                    .fillMaxWidth()
                    .weight(1f, fill = false)
                    .heightIn(max = 480.dp)
                    .horizontalScroll(rememberScrollState()),
            ) {
                Row(
                    modifier = Modifier
                        .background(Color(0xFFF3F6FA), RoundedCornerShape(topStart = 8.dp, topEnd = 8.dp))
                        .border(
                            BorderStroke(1.dp, StockColors.Border),
                            RoundedCornerShape(topStart = 8.dp, topEnd = 8.dp),
                        )
                        .padding(horizontal = 8.dp, vertical = 8.dp),
                    verticalAlignment = Alignment.CenterVertically,
                ) {
                    HistoryCol("推荐日期", 128.dp, alignStart = true)
                    HistoryCol("推荐股票", 100.dp, alignStart = true)
                    HistoryCol("推荐价", 72.dp)
                    HistoryCol("现价", 72.dp)
                    HistoryCol("涨幅率", 76.dp)
                    HistoryCol("买入范围", 92.dp)
                    HistoryCol("卖出范围", 92.dp)
                }
                if (rows.isEmpty()) {
                    Text(
                        if (dates.isEmpty()) "暂无推荐记录" else "该日暂无推荐记录",
                        color = StockColors.TextSecondary,
                        fontSize = 13.sp,
                        modifier = Modifier
                            .width(632.dp)
                            .padding(vertical = 28.dp),
                        textAlign = TextAlign.Center,
                    )
                } else {
                    Column(
                        modifier = Modifier
                            .width(632.dp)
                            .heightIn(max = 420.dp)
                            .verticalScroll(rememberScrollState()),
                    ) {
                        rows.forEach { row ->
                            HistoryTableRow(
                                row = row,
                                currentPrice = currentPrices[row.code],
                                priceLoading = priceLoading,
                                onSelect = {
                                    if (!row.emptyRun && row.code.isNotBlank()) {
                                        onSelectCode(row.code, row.name)
                                    }
                                },
                            )
                        }
                    }
                }
            }
        }
    }
}

private fun flattenHistoryRows(dayEntries: List<RecommendHistorySnapshot>): List<RecommendHistoryRow> {
    val out = ArrayList<RecommendHistoryRow>()
    dayEntries.forEach { snap ->
        if (snap.stocks.isEmpty()) {
            out += RecommendHistoryRow(
                key = "empty-${snap.atMs}",
                atMs = snap.atMs,
                code = "",
                name = "空仓（无入选）",
                recommendPrice = 0.0,
                buyLow = 0.0,
                buyHigh = 0.0,
                sellLow = 0.0,
                sellHigh = 0.0,
                emptyRun = true,
            )
        } else {
            snap.stocks.forEachIndexed { idx, s ->
                out += RecommendHistoryRow(
                    key = "${snap.atMs}-${s.code}-$idx",
                    atMs = snap.atMs,
                    code = s.code,
                    name = s.name.ifBlank { s.code },
                    recommendPrice = s.price,
                    buyLow = s.buyLow,
                    buyHigh = s.buyHigh,
                    sellLow = s.sellLow,
                    sellHigh = s.sellHigh,
                )
            }
        }
    }
    return out
}

@Composable
private fun HistoryTableRow(
    row: RecommendHistoryRow,
    currentPrice: Double?,
    priceLoading: Boolean,
    onSelect: () -> Unit,
) {
    val rec = formatHistoryPrice(row.recommendPrice)
    val curPx = currentPrice?.takeIf { it > 0 }
    val cur = when {
        row.emptyRun -> "--"
        priceLoading && curPx == null -> "…"
        curPx != null -> formatHistoryPrice(curPx)
        else -> "--"
    }
    val recPx = row.recommendPrice
    val chgPct = if (!row.emptyRun && curPx != null && recPx > 0) {
        (curPx - recPx) / recPx * 100.0
    } else {
        null
    }
    val moveColor = pnlColor(chgPct)
    val chgText = when {
        row.emptyRun -> "--"
        chgPct == null -> if (priceLoading) "…" else "--"
        else -> formatChgPct(chgPct)
    }
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .border(BorderStroke(1.dp, Color(0xFFE8EEF7)))
            .clickable(enabled = !row.emptyRun, onClick = onSelect)
            .padding(horizontal = 8.dp, vertical = 9.dp),
        verticalAlignment = Alignment.CenterVertically,
    ) {
        Text(
            formatRecommendHistoryTime(row.atMs),
            color = StockColors.TextSecondary,
            fontSize = 11.sp,
            modifier = Modifier.width(128.dp),
            maxLines = 1,
        )
        Column(modifier = Modifier.width(100.dp)) {
            Text(
                row.name,
                color = if (row.emptyRun) StockColors.TextPrimary else StockColors.PrimaryBlue,
                fontSize = 12.sp,
                fontWeight = FontWeight.SemiBold,
                maxLines = 1,
                overflow = TextOverflow.Ellipsis,
            )
            if (!row.emptyRun) {
                Text(
                    "${row.code} · 点进详情",
                    color = StockColors.TextSecondary,
                    fontSize = 10.sp,
                    maxLines = 1,
                )
            }
        }
        Text(
            if (row.emptyRun) "--" else rec,
            color = Color.Black,
            fontSize = 11.sp,
            fontWeight = FontWeight.SemiBold,
            modifier = Modifier.width(72.dp),
            textAlign = TextAlign.End,
            maxLines = 1,
            overflow = TextOverflow.Ellipsis,
        )
        Text(
            cur,
            color = if (chgPct == null) StockColors.TextSecondary else moveColor,
            fontSize = 11.sp,
            fontWeight = FontWeight.SemiBold,
            modifier = Modifier.width(72.dp),
            textAlign = TextAlign.End,
            maxLines = 1,
            overflow = TextOverflow.Ellipsis,
        )
        Text(
            chgText,
            color = if (chgPct == null) StockColors.TextSecondary else moveColor,
            fontSize = 11.sp,
            fontWeight = FontWeight.SemiBold,
            modifier = Modifier.width(76.dp),
            textAlign = TextAlign.End,
            maxLines = 1,
        )
        Text(
            if (row.emptyRun) "--" else formatPriceRange(row.buyLow, row.buyHigh),
            color = StockColors.PrimaryBlue,
            fontSize = 11.sp,
            modifier = Modifier.width(92.dp),
            textAlign = TextAlign.End,
            maxLines = 1,
            overflow = TextOverflow.Ellipsis,
        )
        Text(
            if (row.emptyRun) "--" else formatPriceRange(row.sellLow, row.sellHigh),
            color = StockColors.UpRed,
            fontSize = 11.sp,
            modifier = Modifier.width(92.dp),
            textAlign = TextAlign.End,
            maxLines = 1,
            overflow = TextOverflow.Ellipsis,
        )
    }
}

private fun pnlColor(chgPct: Double?): Color = when {
    chgPct == null -> Color.Black
    chgPct > 0.005 -> StockColors.UpRed
    chgPct < -0.005 -> StockColors.DownGreen
    else -> Color.Black
}

private fun formatChgPct(pct: Double): String = when {
    pct > 0.005 -> "+${formatDecimal(pct)}%"
    pct < -0.005 -> "${formatDecimal(pct)}%"
    else -> "0.00%"
}

@Composable
private fun HistoryCol(
    label: String,
    width: Dp,
    alignStart: Boolean = false,
) {
    Text(
        label,
        color = StockColors.TextSecondary,
        fontSize = 11.sp,
        fontWeight = FontWeight.SemiBold,
        modifier = Modifier.width(width),
        textAlign = if (alignStart) TextAlign.Start else TextAlign.End,
    )
}
